/**
 * Internal Toolbar — the `internal-tools` chunk entry.
 *
 * A thin, bottom-anchored devtools bar (Base UI) the app shell binds once. It
 * surfaces the three payloads serialized to the client at bootstrap:
 *   • Feature flags — interactive: toggle any loaded flag, reactive everywhere.
 *   • User          — the authenticated user (read-only).
 *   • Config        — the app's global config (read-only).
 *
 * This is a trial run for a broader, extractable pattern. Panels are declared
 * in one registry so adding a surface later is a one-line change. Gating +
 * lazy loading live in InternalToolbarLoader so this whole module (and its
 * deps) stay out of non-internal users' bundles.
 */
import React, { useState } from 'react'
import classnames from 'classnames/bind'
import { Tabs } from '@base-ui-components/react/tabs'
import { Icon } from '@adc/ui-morpheus'
import { FlagsPanel } from './FlagsPanel'
import { UserPanel, ConfigPanel } from './StatePanels'
import { useFlagOverrides } from './useFlagOverrides'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface PanelDef {
  readonly id: string
  readonly label: string
  readonly icon: string
  readonly render: () => React.ReactNode
}

// Panel registry — extend here to add a new toolbar surface.
const PANELS: ReadonlyArray<PanelDef> = [
  { id: 'flags', label: 'Flags', icon: 'flag', render: () => <FlagsPanel /> },
  { id: 'user', label: 'User', icon: 'user', render: () => <UserPanel /> },
  {
    id: 'config',
    label: 'Config',
    icon: 'settings',
    render: () => <ConfigPanel />,
  },
]

const InternalToolbar: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<string>(PANELS[0].id)
  // Surface override count on the collapsed pill so an active override is
  // never invisible.
  const { overriddenCount } = useFlagOverrides()

  return (
    <div className={cx('toolbar-root')} data-open={open}>
      {open && (
        <div
          className={cx('toolbar-panel')}
          role='dialog'
          aria-label='Internal toolbar'
        >
          <Tabs.Root value={active} onValueChange={v => setActive(String(v))}>
            <Tabs.List className={cx('toolbar-tabs')}>
              {PANELS.map(p => (
                <Tabs.Tab key={p.id} value={p.id} className={cx('toolbar-tab')}>
                  <Icon name={p.icon} className={cx('toolbar-tab-icon')} />
                  {p.label}
                </Tabs.Tab>
              ))}
              <Tabs.Indicator className={cx('toolbar-tab-indicator')} />
            </Tabs.List>
            {PANELS.map(p => (
              <Tabs.Panel
                key={p.id}
                value={p.id}
                className={cx('toolbar-tab-panel')}
              >
                {p.render()}
              </Tabs.Panel>
            ))}
          </Tabs.Root>
        </div>
      )}

      <button
        type='button'
        className={cx('toolbar-pill')}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label='Toggle internal toolbar'
      >
        <Icon name='code' className={cx('toolbar-pill-icon')} />
        <span className={cx('toolbar-pill-label')}>Internal</span>
        {overriddenCount > 0 && (
          <span className={cx('toolbar-pill-badge')}>{overriddenCount}</span>
        )}
      </button>
    </div>
  )
}

export default InternalToolbar
